/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 地理数据处理工具（使用普通 JS 对象）
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-25
 */

/**
 * 写在前面
 * 团队水印条目中的【普通预设选项】和【地址关联预设选项】的协调处理，两者的内容应保持一致,双向对齐
 * 分为2种情况：
 * 1. Excel导入新的含地址条目
 * 2. 用户修改不含地址条目
 *
 * newGeoItemsList = geoItemsList + pendingItemsList + plainItemsList(转换为GeoItem结构) 去除重复
 * newPlainItemsList = plainItemsList + newGeoItemsList.content 去除重复
 */

// 地理位置坐标
interface Location {
  lat: string
  lng: string
}

// 地理数据条目
export interface GeoItem {
  watermarkID: string
  itemID: string | number
  content: string
  uploadLocation: Location[]
}

// Excel 导入的待处理条目
interface PendingItem {
  content: string
  lat: string
  lng: string
  status?: number
}

// 水印条目
interface WatermarkItem {
  id: string | number
  editType: number
  contentEdit?: {
    itemsList?: string[]
  }
}

// 水印内容
interface WatermarkContent {
  id: string
  items: WatermarkItem[]
}

/**
 * Excel导入新的含地址条目
 * @param watermarkId 水印ID
 * @param itemId 水印条目ID
 * @param plainItemsList 用户录入的不含地址条目
 * @param geoItemsList 现有地址关联预设选项 watermarkList[index].itemsList
 * @param pendingItemsList 新的含地址条目 excel导入
 */
export const coordinateByPendingItemsList = (
  watermarkId: string,
  itemId: string | number,
  plainItemsList: string[],
  geoItemsList: GeoItem[],
  pendingItemsList: PendingItem[] = []
): { newGeoItemsList: GeoItem[]; newPlainItemsList: string[] } => {
  let newGeoItemsList: GeoItem[] = [...geoItemsList]

  // 遍历Excel上传的含地址条目，结合现有geoItemsList内容，生成 newGeoItemsList
  for (let i = 0; i < pendingItemsList.length; i++) {
    const curItem = pendingItemsList[i]
    //是否存在同名的条目
    const existItemIndex = newGeoItemsList.findIndex(
      (item) =>
        item.watermarkID === watermarkId &&
        item.itemID === itemId &&
        item.content === curItem.content
    )
    if (existItemIndex > -1) {
      //有同名条目，插入到uploadLocation
      newGeoItemsList = newGeoItemsList.map((item, idx) =>
        idx === existItemIndex
          ? {
              ...item,
              uploadLocation: [
                ...item.uploadLocation,
                { lat: curItem.lat, lng: curItem.lng }
              ]
            }
          : item
      )
    } else {
      //无同名条目，创建一个条目，插入到itemsList
      newGeoItemsList = [
        ...newGeoItemsList,
        {
          watermarkID: watermarkId,
          itemID: itemId,
          content: curItem.content,
          uploadLocation: [{ lat: curItem.lat, lng: curItem.lng }]
        }
      ]
    }
  }

  // 遍历plainItemsList，如果newGeoItemsList有不存在的plainItem, 插入到newGeoItemsList, uploadLocation为空
  for (let i = 0; i < plainItemsList.length; i++) {
    const curPlainItem = plainItemsList[i]
    //是否存在同名的条目
    const existItemIndex = newGeoItemsList.findIndex(
      (item) =>
        item.watermarkID === watermarkId &&
        item.itemID === itemId &&
        item.content === curPlainItem
    )
    if (existItemIndex === -1) {
      //无同名条目，创建一个条目，插入到newGeoItemsList，uploadLocation为空； 有同名条目，不做处理
      newGeoItemsList = [
        ...newGeoItemsList,
        {
          watermarkID: watermarkId,
          itemID: itemId,
          content: curPlainItem,
          uploadLocation: []
        }
      ]
    }
  }

  //newGeoItemsList插入到plainItemsList，去重
  const curItemGeoItemsList = newGeoItemsList.filter(
    (item) => item.watermarkID === watermarkId && item.itemID === itemId
  )

  const newPlainItemsList = Array.from(
    new Set([
      ...plainItemsList,
      ...curItemGeoItemsList.map((item) => item.content)
    ])
  )

  return {
    newGeoItemsList,
    newPlainItemsList
  }
}

/**
 * 用户录入不含地址条目，根据plainItemsList，更新geoItemsList
 * @param watermarkId 水印ID
 * @param itemId 水印条目ID
 * @param geoItemsList 现有地址关联预设选项 watermarkList[index].itemsList
 * @param plainItemsList 用户录入的不含地址条目
 */
export const coordinateByPlainItemsList = (
  watermarkId: string,
  itemId: string | number,
  geoItemsList: GeoItem[],
  plainItemsList: string[] = []
): GeoItem[] => {
  let newGeoItemsList: GeoItem[] = [...geoItemsList]

  // 遍历plainItemsList，如果有新增的plainItem, 插入到newGeoItemsList, uploadLocation为空
  for (let i = 0; i < plainItemsList.length; i++) {
    const curPlainItem = plainItemsList[i]
    //是否存在同名的条目
    const existItemIndex = newGeoItemsList.findIndex(
      (item) =>
        item.watermarkID === watermarkId &&
        item.itemID === itemId &&
        item.content === curPlainItem
    )
    if (existItemIndex === -1) {
      //无同名条目，创建一个条目，插入到newGeoItemsList，uploadLocation为空； 有同名条目，不做处理
      newGeoItemsList = [
        ...newGeoItemsList,
        {
          watermarkID: watermarkId,
          itemID: itemId,
          content: curPlainItem,
          uploadLocation: []
        }
      ]
    }
  }

  //根据plainItemsList, 去除newGeoItemsList中不存在的条目
  newGeoItemsList = newGeoItemsList.filter((item) => {
    if (item.watermarkID === watermarkId && item.itemID === itemId) {
      return plainItemsList.includes(item.content)
    }
    return true
  })

  return newGeoItemsList
}

/**
 * 加载水印时，根据每个选项条目的内容，校准geoItemsList
 * plainItems有的，geoItemsList没有的，插入到geoItemsList
 * plainItems没有的，geoItemsList有的，删除geoItemsList
 */
export const coordinateBeforeEditAndSubmit = (
  watermark: WatermarkContent,
  geoItemsList: GeoItem[]
): GeoItem[] => {
  const watermarkId = watermark.id
  //可编辑内容条目，且内容类型为【选项】
  // 090版本增加了【多级选项】,editType = 3,因为产品要求切换类型时，不清空内容，因此改为不判断类型，而判断itemsList是否为空
  const watermarkItems = watermark.items.filter(
    (item) =>
      (item.editType === 2 || item.editType === 3) &&
      // item.contentEdit.editType === 2
      (item.contentEdit?.itemsList?.length ?? 0) > 0
  )
  //根据plainItemsList, 去除geoItemsList中不存在的条目
  let newGeoItemsList: GeoItem[] = geoItemsList.filter((geoItem) => {
    return watermarkItems.some((watermarkItem) => {
      return (
        geoItem.watermarkID === watermarkId &&
        geoItem.itemID === watermarkItem.id &&
        watermarkItem.contentEdit?.itemsList?.includes(geoItem.content)
      )
    })
  })

  //如果geoItemsList中不存在watermarkItems中的条目，插入到newGeoItemsList
  watermarkItems.forEach((watermarkItem) => {
    watermarkItem.contentEdit?.itemsList?.forEach((item) => {
      const existItemIndex = newGeoItemsList.findIndex(
        (geoItem) =>
          geoItem.watermarkID === watermarkId &&
          geoItem.itemID === watermarkItem.id &&
          geoItem.content === item
      )
      if (existItemIndex === -1) {
        //无同名条目，创建一个条目，插入到newGeoItemsList，uploadLocation为空； 有同名条目，不做处理
        newGeoItemsList = [
          ...newGeoItemsList,
          {
            watermarkID: watermarkId,
            itemID: watermarkItem.id,
            content: item,
            uploadLocation: []
          }
        ]
      }
    })
  })
  return newGeoItemsList
}

export const checkGeoItemsList = (
  geoItemsList: GeoItem[]
): { hasOver8Locations: boolean; reducedGeoItemsList: GeoItem[] } => {
  const hasOver8Locations = geoItemsList.some(
    (item) => (item.uploadLocation?.length ?? 0) > 8
  )

  if (hasOver8Locations) {
    const reducedGeoItemsList = geoItemsList.map((item) => ({
      ...item,
      uploadLocation: item.uploadLocation?.slice(-8) || []
    }))
    return { hasOver8Locations, reducedGeoItemsList }
  } else {
    return { hasOver8Locations, reducedGeoItemsList: geoItemsList }
  }
}
